import { HorizontalRule, TrendingDown, TrendingUp } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import {
	UTILITY_CONSUMPTION_WINDOW,
	type UtilityConsumptionMeterGap,
	type UtilityConsumptionSummary,
	type UtilityConsumptionWindow,
} from "../../models/UtilityConsumption";
import { formatVolume, VOLUME_UNIT } from "../../utils/format";
import {
	CONSUMPTION_CAPTION_SX,
	CONSUMPTION_FOCUS_RING_SX,
	CONSUMPTION_UNIT_COLOR,
} from "../../utils/tokens";
import { getMeterGapNote } from "../../utils/meterGaps";
import { ConsumptionMetric } from "../ConsumptionMetric";
import { ConsumptionCard, metricRowSx } from "./ConsumptionCard";

const getTrendProperties = (changePercent: number | null) => {
	if (changePercent === null) {
		return {
			Icon: HorizontalRule,
			color: CONSUMPTION_UNIT_COLOR,
			percentage: "",
			label: "Comparison unavailable",
			tooltip:
				"Comparison is unavailable because one of the periods has no complete value.",
		};
	}

	const percentage = `${Math.abs(changePercent).toLocaleString("en-US", {
		maximumFractionDigits: 1,
	})}%`;

	const color =
		Math.abs(changePercent) < 5
			? CONSUMPTION_UNIT_COLOR
			: changePercent < 0
				? "#2E7D32"
				: "#C62828";

	const increasing = changePercent > 0;
	const decreasing = changePercent < 0;

	const label = decreasing
		? `${percentage} decrease`
		: increasing
			? `${percentage} increase`
			: "0% change";

	return {
		Icon: decreasing ? TrendingDown : increasing ? TrendingUp : HorizontalRule,
		color,
		percentage,
		label,
		tooltip: `${label} compared with the previous 7 complete days. Changes below 5% are treated as normal variation.`,
	};
};

export const ConsumptionTrendCard = ({
	summary,
	meterGaps,
	plantTimeZone,
}: {
	summary: UtilityConsumptionSummary;
	meterGaps: UtilityConsumptionMeterGap[];
	plantTimeZone: string;
}) => {
	const trend = getTrendProperties(summary.SevenDayChangePercent);
	const gapOf = (windowCode: UtilityConsumptionWindow) =>
		getMeterGapNote(meterGaps, windowCode, plantTimeZone);
	const last7Gap = gapOf(UTILITY_CONSUMPTION_WINDOW.LAST_7);
	const previous7Gap = gapOf(UTILITY_CONSUMPTION_WINDOW.PREVIOUS_7);
	const thirtyDayGap = gapOf(UTILITY_CONSUMPTION_WINDOW.THIRTY_DAY);

	return (
		<ConsumptionCard
			area="comparison"
			label="Seven day consumption trend"
			upper={
				<Box
					sx={{
						alignItems: "flex-end",
						display: "flex",
						flexWrap: "wrap",
						gap: 1,
						justifyContent: "space-between",
					}}
				>
					<ConsumptionMetric
						label="Last 7 days"
						lead
						value={formatVolume(summary.Last7Gallons)}
						unit={VOLUME_UNIT}
						secondary={last7Gap?.secondary}
						tooltip={last7Gap?.tooltip}
					/>
					<Tooltip describeChild title={trend.tooltip}>
						<Box
							tabIndex={0}
							sx={{
								alignItems: "flex-end",
								display: "flex",
								flexDirection: "column",
								minWidth: 0,
								outline: "none",
								textAlign: "right",
								"&:focus-visible": CONSUMPTION_FOCUS_RING_SX,
							}}
						>
							<Box
								sx={{
									alignItems: "center",
									color: trend.color,
									display: "flex",
									gap: 0.5,
								}}
							>
								<trend.Icon aria-hidden="true" sx={{ fontSize: "1.5rem" }} />
								<Typography
									aria-hidden="true"
									sx={{
										fontSize: "1.5rem",
										fontVariantNumeric: "tabular-nums",
										fontWeight: 600,
										lineHeight: 1,
									}}
								>
									{trend.percentage}
								</Typography>
							</Box>
							<Typography component="div" sx={CONSUMPTION_CAPTION_SX}>
								vs previous 7 days
							</Typography>
						</Box>
					</Tooltip>
				</Box>
			}
			lower={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="Previous 7 days"
						secondary={previous7Gap?.secondary}
						tooltip={previous7Gap?.tooltip}
						value={formatVolume(summary.Previous7Gallons)}
						unit={VOLUME_UNIT}
					/>
					<ConsumptionMetric
						label="30-day daily average"
						secondary={thirtyDayGap?.secondary}
						tooltip={thirtyDayGap?.tooltip}
						value={formatVolume(summary.ThirtyDayAverageGallons)}
						unit="gal/day"
					/>
				</Box>
			}
		/>
	);
};
